import React from 'react';
import {
  Email,
  EmailOutlined,
  Phone,
  PhoneDisabled,
  Sms,
  SmsOutlined,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type ContactMethodPreference,
  enhancedApi,
} from '@store/generated/patients';
import { useSnackbar } from 'notistack';

// Enhanced API with cache invalidation
const communicationPreferencesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: ['CommunicationPreferences'],
  endpoints: {
    getV2ProviderPatientsByPatientIdCommunicationsPreferences: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'CommunicationPreferences', id: patientId },
      ],
    },
    putV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKey: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'CommunicationPreferences', id: patientId },
      ],
    },
  },
});

const {
  useGetV2ProviderPatientsByPatientIdCommunicationsPreferencesQuery,
  usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKeyMutation,
} = communicationPreferencesApi;

interface PatientCommunicationPreferencesProps {
  patientId: string;
  mobilePhone?: string;
  email?: string;
}

const PREFERENCE_CYCLE: ContactMethodPreference[] = ['preferred', 'neutral', 'avoid'];

const getNextPreference = (current: ContactMethodPreference | undefined): ContactMethodPreference => {
  if (!current) return 'neutral';
  const currentIndex = PREFERENCE_CYCLE.indexOf(current);
  const nextIndex = (currentIndex + 1) % PREFERENCE_CYCLE.length;
  return PREFERENCE_CYCLE[nextIndex];
};

export const PatientCommunicationPreferences: React.FC<PatientCommunicationPreferencesProps> = ({
  patientId,
  mobilePhone,
  email,
}) => {
  const { data, isLoading, error } =
    useGetV2ProviderPatientsByPatientIdCommunicationsPreferencesQuery({
      patientId,
    });

  const [updatePreference] = usePutV2ProviderPatientsByPatientIdCommunicationsPreferencesAndPreferenceKeyMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handlePreferenceClick = async (preferenceKey: 'sms' | 'email' | 'phone_call') => {
    const currentValue = data?.[preferenceKey];
    const nextValue = getNextPreference(currentValue);

    try {
      await updatePreference({
        patientId,
        preferenceKey,
        updateCommunicationPreferenceRequest: {
          preference_value: nextValue,
        },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update preference:', err);
      enqueueSnackbar('Failed to update communication preference', { variant: 'error' });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error">
        Failed to load communication preferences
      </Typography>
    );
  }

  const smsPreference = data?.sms || 'neutral';
  const emailPreference = data?.email || 'neutral';
  const phonePreference = data?.phone_call || 'neutral';

  const getIconColor = (preference: ContactMethodPreference) => {
    switch (preference) {
      case 'preferred':
        return 'primary.main';
      case 'avoid':
        return 'error.main';
      case 'neutral':
      default:
        return 'text.secondary';
    }
  };

  const getTooltipText = (
    method: string,
    preference: ContactMethodPreference,
    contactInfo?: string,
  ) => {
    // Only show preference if it's not neutral
    const preferenceText = preference !== 'neutral'
      ? `${preference.charAt(0).toUpperCase() + preference.slice(1)} - `
      : '';
    const contactText = contactInfo || '';
    return `${method}: ${preferenceText}${contactText} (click to change)`;
  };

  return (
    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      {/* SMS */}
      <Tooltip title={getTooltipText('SMS', smsPreference, mobilePhone)}>
        <IconButton
          onClick={() => handlePreferenceClick('sms')}
          size="small"
          sx={{ color: getIconColor(smsPreference) }}
        >
          {smsPreference === 'preferred' ? <Sms /> : <SmsOutlined />}
        </IconButton>
      </Tooltip>

      {/* Email */}
      <Tooltip title={getTooltipText('Email', emailPreference, email)}>
        <IconButton
          onClick={() => handlePreferenceClick('email')}
          size="small"
          sx={{ color: getIconColor(emailPreference) }}
        >
          {emailPreference === 'preferred' ? <Email /> : <EmailOutlined />}
        </IconButton>
      </Tooltip>

      {/* Phone */}
      <Tooltip title={getTooltipText('Phone', phonePreference, mobilePhone)}>
        <IconButton
          onClick={() => handlePreferenceClick('phone_call')}
          size="small"
          sx={{ color: getIconColor(phonePreference) }}
        >
          {phonePreference === 'avoid' ? <PhoneDisabled /> : <Phone />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};
